import { Popover } from '@snowball-tech/fractal/dist/src/components/Popover'
import { Tabs as FractalTabs } from '@snowball-tech/fractal/dist/src/components/Tabs'
import { cj } from '@snowball-tech/fractal/dist/src/styles/helpers'

import { type ComponentProps, type ReactNode, Fragment, useState } from 'react'

import isEmpty from 'lodash/fp/isEmpty'

import type {
  ChildrenProps,
  MediaProviderTabContentProps,
  MediaTypes,
  OnPick,
} from '../../../types'

import { AllowedMediaSources } from '../../../enums'
import { type HandleFileCallback, validateSource } from '../../../helpers/files'
import { ImageSize } from '../../../types'
import Delete from './Delete'
import Link from './Link'
import Paste from './Paste'
import Tabs from './Tabs'
import Upload from './Upload'

const PROVIDERS_WITH_GALLERY: Set<string> = new Set([
  AllowedMediaSources.Giphy,
  AllowedMediaSources.LoremPicsum,
  AllowedMediaSources.Unsplash,
])

type Props = {
  setIsLoading: MediaProviderTabContentProps['setIsLoading']
  trigger: ReactNode
  type: MediaTypes
  allowedSources?: Array<AllowedMediaSources>
  deletable?: boolean
  editable?: boolean
  fullWidth?: boolean
  handleFile?: HandleFileCallback
  setProgress?: MediaProviderTabContentProps['setProgress']
  side?: ComponentProps<typeof Popover>['side']
  withPicker?: boolean
  onChange?: OnPick
} & Partial<ChildrenProps>
export default function FilePicker({
  allowedSources = [
    AllowedMediaSources.Paste,
    AllowedMediaSources.Upload,
    AllowedMediaSources.Link,
  ],
  children,
  deletable = true,
  editable = true,
  fullWidth = true,
  handleFile,
  onChange,
  setIsLoading,
  setProgress,
  side,
  trigger,
  type,
  withPicker = true,
}: Props) {
  const validAllowedSources = allowedSources.filter((source) =>
    validateSource(source, type),
  )
  const tabbedAllowedSources = validAllowedSources.filter(
    (provider) => provider !== AllowedMediaSources.Paste,
  )
  const defaultTab = tabbedAllowedSources[0]

  const [currentTab, setCurrentTab] = useState(
    defaultTab ?? (deletable ? 'delete' : ''),
  )

  const [isPopoverOpen, setIsPopoverOpen] = useState(false)

  const hasTabs =
    deletable ||
    allowedSources.includes(AllowedMediaSources.Paste) ||
    !isEmpty(validAllowedSources)
  const isPopoverDisabled = !editable || !withPicker || !hasTabs
  const isPasteAllowed = allowedSources.includes(AllowedMediaSources.Paste)

  const handleUpdate = async (
    newUrl: string,
    provider: string,
    meta?: { duration: number } | { thumbnailUrl: string } | ImageSize,
    silent?: boolean,
  ) => {
    if (!silent) {
      setIsLoading?.(true)
    }

    const shouldClosePopover = [
      AllowedMediaSources.Paste,
      AllowedMediaSources.Upload,
      'delete',
    ].includes(provider)
    if (shouldClosePopover) {
      setIsPopoverOpen(false)
    }

    return onChange?.(newUrl, provider, meta)
  }

  const handlePopoverClose = () => {
    if (currentTab === 'delete') {
      setCurrentTab(defaultTab ?? (deletable ? 'delete' : ''))
    }
  }

  return (
    <Popover
      className={cj(
        '[&>[type="button"]]:pointer-events-none [&>[type="button"]]:justify-center',
        fullWidth ? '' : 'mx-auto',
      )}
      disabled={isPopoverDisabled}
      fullWidth={fullWidth}
      open={isPopoverOpen}
      popover={{
        className: cj(
          'min-w-[calc(var(--radix-popover-trigger-width)*0.75)] max-w-[calc(var(--size-breakpoint-md)*0.75)]',
          PROVIDERS_WITH_GALLERY.has(currentTab)
            ? 'h-[calc(var(--radix-popover-content-available-height)-theme(spacing.2))]'
            : 'h-full',
        ),
      }}
      side={side}
      trigger={trigger}
      triggerAsButton={false}
      withArrow={false}
      withScroll={false}
      onClose={handlePopoverClose}
      onOpenChange={setIsPopoverOpen}
    >
      <div className="flex h-full flex-col gap-2">
        {hasTabs && (
          <FractalTabs
            className="h-full min-h-0 gap-2"
            tab={currentTab}
            tabs={
              <Tabs
                allowedSources={tabbedAllowedSources}
                showDeleteTab={deletable}
                type={type}
              />
            }
            onTabChange={setCurrentTab}
          >
            <div className="h-full min-h-0">
              {allowedSources.map((provider) => (
                <Fragment key={provider}>
                  {provider === AllowedMediaSources.Upload && (
                    <Upload
                      handleFile={handleFile}
                      setIsLoading={setIsLoading}
                      setProgress={setProgress}
                      type={type}
                      onPick={handleUpdate}
                    />
                  )}

                  {provider === AllowedMediaSources.Link && (
                    <Link
                      setIsLoading={setIsLoading}
                      type={type}
                      onPick={handleUpdate}
                    />
                  )}
                </Fragment>
              ))}

              {children}

              <Delete enabled={deletable} type={type} onDelete={handleUpdate} />
            </div>
          </FractalTabs>
        )}

        <Paste
          active={isPopoverOpen}
          enabled={isPasteAllowed}
          handleFile={handleFile}
          setIsLoading={setIsLoading}
          setProgress={setProgress}
          type={type}
          onPaste={handleUpdate}
        />
      </div>
    </Popover>
  )
}
